import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';

import {
  ProfileLanguageStoreService,
  ProfileStoreService,
} from '@customer-portal/data-access/settings';
import { getToastContentBySeverity } from '@customer-portal/shared/helpers/custom-toast';
import {
  ServiceNowLanguage,
  ToastSeverity,
} from '@customer-portal/shared/models';

import { LoggingService } from '../app-insights';
import {
  ServiceNowCaseDetailsId,
  ServiceNowTable,
} from './service-now.constants';
import {
  BaseServiceNowParams,
  CertificateParams,
  CompanySettingsParams,
  InvoiceParams,
  NotificationActionParms,
  ScheduleParams,
  SitesSettingsParams,
  SitesSettingsSupportInput,
} from './service-now.models';
import { ServiceNowService } from './service-now.service';

@Injectable({
  providedIn: 'root',
})
export class ServiceNowFeatureService {
  private readonly serviceNowService = inject(ServiceNowService);
  private readonly messageService = inject(MessageService);
  private readonly loggingService = inject(LoggingService);
  private readonly ts = inject(TranslocoService);
  private readonly profileLanguageStoreService = inject(
    ProfileLanguageStoreService,
  );
  private readonly profileStoreService = inject(ProfileStoreService);

  openServiceNowGeneralHelpSupport(): void {
    try {
      const helpParams: BaseServiceNowParams = {
        ...this.getLanguageParams(),
        reportingCountry:
          this.profileStoreService.profileInformation().countryCode,
        OSCContactID: this.profileStoreService.oscContactId(),
      };
      this.serviceNowService.openCatalogItemSupport(helpParams);
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  openNotificationOrActionSupport(snowLink?: string): void {
    try {
      if (snowLink) {
        this.openActionNotificationWithLink(snowLink);
      } else {
        this.openCatalogSupport();
      }
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  openScheduleSupport(
    scheduleParams: Omit<
      ScheduleParams,
      'language' | 'communicationLanguageCode' | 'OSCContactID'
    >,
  ): void {
    try {
      const scheduleParamWithLanguage: ScheduleParams = {
        ...scheduleParams,
        ...this.getLanguageParams(),
        OSCContactID: this.profileStoreService.oscContactId(),
      };
      this.serviceNowService.openScheduleSupport(scheduleParamWithLanguage);
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  openInvoiceSupport(
    invoiceParams: Omit<
      InvoiceParams,
      'language' | 'communicationLanguageCode' | 'OSCContactID'
    >,
  ): void {
    try {
      const invoiceParamsWithLanguage: InvoiceParams = {
        ...invoiceParams,
        ...this.getLanguageParams(),
        OSCContactID: this.profileStoreService.oscContactId(),
      };
      this.serviceNowService.openInvoiceSupport(invoiceParamsWithLanguage);
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  openCertificateSupport(
    certificateParams: Omit<
      CertificateParams,
      'language' | 'communicationLanguageCode' | 'OSCContactID'
    >,
  ): void {
    try {
      const completeParams: CertificateParams = {
        ...certificateParams,
        ...this.getLanguageParams(),
        OSCContactID: this.profileStoreService.oscContactId(),
      };
      this.serviceNowService.openCertificateSupport(completeParams);
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  openCompanySettingsSupport(
    companySettingsParams: Omit<
      CompanySettingsParams,
      'language' | 'communicationLanguageCode' | 'OSCContactID'
    >,
  ): void {
    try {
      const completeParams: CompanySettingsParams = {
        ...companySettingsParams,
        ...this.getLanguageParams(),
        OSCContactID: this.profileStoreService.oscContactId(),
      };
      this.serviceNowService.openCompanySettingsSupport(completeParams);
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  openSitesSettingsSupport(
    sitesSettingsParams: SitesSettingsSupportInput,
  ): void {
    try {
      const completeParams: SitesSettingsParams = {
        ...sitesSettingsParams,
        ...this.getLanguageParams(),
        reportingCountry:
          this.profileStoreService.profileInformation().countryCode,
        OSCContactID: this.profileStoreService.oscContactId(),
      };
      this.serviceNowService.openSitesSettingsSupport(completeParams);
    } catch (error) {
      this.handleServiceNowError(error);
    }
  }

  private openActionNotificationWithLink(snowLink: string): void {
    const params: NotificationActionParms = {
      language: this.profileLanguageStoreService.portalLanguage(),
      table: ServiceNowTable,
      id: ServiceNowCaseDetailsId,
      sys_id: snowLink,
    };
    this.serviceNowService.openActionNotificationItemSupport(params);
  }

  private openCatalogSupport(): void {
    const helpParams: BaseServiceNowParams = {
      ...this.getLanguageParams(),
      reportingCountry:
        this.profileStoreService.profileInformation().countryCode,
      OSCContactID: this.profileStoreService.oscContactId(),
    };
    this.serviceNowService.openCatalogItemSupport(helpParams);
  }

  private handleServiceNowError(error: unknown): void {
    const message = getToastContentBySeverity(ToastSeverity.Error);
    message.summary = this.ts.translate('serviceNow.error');
    this.messageService.add(message);
    this.loggingService.logException(
      error instanceof Error ? error : new Error(String(error)),
    );
  }

  private getLanguageParams() {
    const profileInfo = this.profileStoreService.profileInformation();
    const portalLanguage = this.profileLanguageStoreService.portalLanguage();

    const { communicationLanguage } = profileInfo;

    const tempLanguageOverrides: Record<string, ServiceNowLanguage> = {
      et: ServiceNowLanguage.EstonianTemp,
      no: ServiceNowLanguage.NorwegianTemp,
      'zh-Hans': ServiceNowLanguage.ChineseSimplifiedTemp,
      'zh-Hant': ServiceNowLanguage.ChineseTraditionalTemp,
    };

    const language = tempLanguageOverrides[portalLanguage] ?? portalLanguage;

    const communicationLanguageCode =
      tempLanguageOverrides[communicationLanguage] ?? communicationLanguage;

    return {
      language,
      communicationLanguageCode,
    };
  }
}
